<template>
  <div class="cms-home-page">
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header d-flex align-items-center justify-content-between">
            <h4 class="card-title mb-0">
              <i class="bi bi-house me-2"></i>Homepage Content Management
            </h4>
            <button class="btn btn-primary" @click="saveChanges">
              <i class="bi bi-save me-2"></i>Save Changes
            </button>
          </div>
          <div class="card-body">
            <!-- Hero Section -->
            <div class="card mb-4">
              <div class="card-header">
                <h5 class="mb-0">Hero Section</h5>
              </div>
              <div class="card-body">
                <div class="mb-3">
                  <label class="form-label">Hero Title</label>
                  <input type="text" class="form-control" v-model="content.hero.title">
                </div>
                <div class="mb-3">
                  <label class="form-label">Hero Subtitle</label>
                  <textarea class="form-control" rows="3" v-model="content.hero.subtitle"></textarea>
                </div>
                <div class="mb-3">
                  <label class="form-label">Call to Action Button Text</label>
                  <input type="text" class="form-control" v-model="content.hero.ctaText">
                </div>
                <div class="mb-3">
                  <label class="form-label">Hero Background Image</label>
                  <div class="input-group">
                    <input type="url" class="form-control" v-model="content.hero.backgroundImage" placeholder="Image URL">
                    <button class="btn btn-outline-secondary" type="button">
                      <i class="bi bi-upload"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- About Section -->
            <div class="card mb-4">
              <div class="card-header">
                <h5 class="mb-0">About Section</h5>
              </div>
              <div class="card-body">
                <div class="mb-3">
                  <label class="form-label">Section Title</label>
                  <input type="text" class="form-control" v-model="content.about.title">
                </div>
                <div class="mb-3">
                  <label class="form-label">Description</label>
                  <textarea class="form-control" rows="5" v-model="content.about.description"></textarea>
                </div>
              </div>
            </div>

            <!-- Features Section -->
            <div class="card mb-4">
              <div class="card-header d-flex justify-content-between align-items-center">
                <h5 class="mb-0">Features Section</h5>
                <button class="btn btn-sm btn-outline-primary" @click="addFeature">
                  <i class="bi bi-plus me-1"></i>Add Feature
                </button>
              </div>
              <div class="card-body">
                <div v-for="(feature, index) in content.features" :key="index" class="border rounded p-3 mb-3">
                  <div class="d-flex justify-content-between align-items-start mb-3">
                    <h6>Feature {{ index + 1 }}</h6>
                    <button class="btn btn-sm btn-outline-danger" @click="removeFeature(index)">
                      <i class="bi bi-trash"></i>
                    </button>
                  </div>
                  <div class="row">
                    <div class="col-md-6">
                      <div class="mb-3">
                        <label class="form-label">Icon Class</label>
                        <input type="text" class="form-control" v-model="feature.icon" placeholder="bi bi-icon-name">
                      </div>
                      <div class="mb-3">
                        <label class="form-label">Title</label>
                        <input type="text" class="form-control" v-model="feature.title">
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="mb-3">
                        <label class="form-label">Description</label>
                        <textarea class="form-control" rows="3" v-model="feature.description"></textarea>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Statistics Section -->
            <div class="card mb-4">
              <div class="card-header">
                <h5 class="mb-0">Statistics Section</h5>
              </div>
              <div class="card-body">
                <div class="row">
                  <div v-for="(stat, index) in content.statistics" :key="index" class="col-md-3">
                    <div class="mb-3">
                      <label class="form-label">{{ stat.label }}</label>
                      <input type="number" class="form-control" v-model="stat.value">
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Contact Section -->
            <div class="card">
              <div class="card-header">
                <h5 class="mb-0">Contact Section</h5>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-md-6">
                    <div class="mb-3">
                      <label class="form-label">Contact Email</label>
                      <input type="email" class="form-control" v-model="content.contact.email">
                    </div>
                    <div class="mb-3">
                      <label class="form-label">Phone Number</label>
                      <input type="tel" class="form-control" v-model="content.contact.phone">
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="mb-3">
                      <label class="form-label">Office Address</label>
                      <textarea class="form-control" rows="4" v-model="content.contact.address"></textarea>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Preview Modal -->
    <div class="modal fade" :class="{ show: showPreview, 'd-block': showPreview }" v-if="showPreview" @click.self="showPreview = false">
      <div class="modal-dialog modal-xl">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Homepage Preview</h5>
            <button type="button" class="btn-close" @click="showPreview = false"></button>
          </div>
          <div class="modal-body">
            <div class="preview-content">
              <div class="hero-preview bg-primary text-white p-5 text-center mb-4">
                <h1>{{ content.hero.title }}</h1>
                <p class="lead">{{ content.hero.subtitle }}</p>
                <button class="btn btn-light">{{ content.hero.ctaText }}</button>
              </div>

              <div class="about-preview mb-4">
                <h2>{{ content.about.title }}</h2>
                <p>{{ content.about.description }}</p>
              </div>

              <div class="features-preview mb-4">
                <div class="row">
                  <div v-for="feature in content.features" :key="feature.title" class="col-md-4 mb-3">
                    <div class="text-center">
                      <i :class="feature.icon" class="display-4 text-primary"></i>
                      <h5 class="mt-2">{{ feature.title }}</h5>
                      <p>{{ feature.description }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CMSHome',
  data() {
    return {
      showPreview: false,
      content: {
        hero: {
          title: 'European & Developing Countries Clinical Trials Partnership',
          subtitle: 'Accelerating the development of new interventions against poverty-related and neglected infectious diseases',
          ctaText: 'Learn More',
          backgroundImage: ''
        },
        about: {
          title: 'About EDCTP',
          description: 'The European & Developing Countries Clinical Trials Partnership (EDCTP) was created to accelerate the development of new or improved drugs, vaccines, microbicides and diagnostics against HIV/AIDS, tuberculosis and malaria, with a focus on phase II and III clinical trials in sub-Saharan Africa.'
        },
        features: [
          {
            icon: 'bi bi-shield-check',
            title: 'Clinical Trials',
            description: 'Supporting high-quality clinical trials across sub-Saharan Africa'
          },
          {
            icon: 'bi bi-people',
            title: 'Capacity Building',
            description: 'Building research capacity and infrastructure'
          },
          {
            icon: 'bi bi-globe',
            title: 'Global Partnership',
            description: 'Fostering collaboration between European and African researchers'
          }
        ],
        statistics: [
          { label: 'Active Projects', value: 156 },
          { label: 'Partner Countries', value: 23 },
          { label: 'Research Centers', value: 45 },
          { label: 'Publications', value: 287 }
        ],
        contact: {
          email: 'info@edctp.org',
          phone: '+31 70 344 0880',
          address: 'Anna van Saksenlaan 51\n2593 HW The Hague\nThe Netherlands'
        }
      }
    }
  },
  methods: {
    addFeature() {
      this.content.features.push({
        icon: 'bi bi-star',
        title: 'New Feature',
        description: 'Feature description'
      })
    },
    removeFeature(index) {
      this.content.features.splice(index, 1)
    },
    saveChanges() {
      console.log('Saving homepage content:', this.content)
      this.$toast?.success('Homepage content saved successfully!')
    },
    previewChanges() {
      this.showPreview = true
    }
  }
}
</script>

<style scoped>
.cms-home-page {
  padding: 1rem 0;
}

.modal.show {
  background-color: rgba(0, 0, 0, 0.5);
}

.preview-content {
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
}

.hero-preview {
  border-radius: 0.375rem;
}

.card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}
</style>
